import { useRef, useEffect } from 'react';
import { useFrame } from '@react-three/fiber';
import { Sphere, Text } from '@react-three/drei';
import * as THREE from 'three';
import type { TransformControls as TransformControlsImpl } from 'three-stdlib';
import { useMotionStore } from './useMotionStore';

interface ControlPointProps {
  blockId: string;
  pointIndex: number;
  position: THREE.Vector3;
  color: string;
  size: number;
  isActive: boolean;
  controlPointRefsRef: React.MutableRefObject<Map<string, THREE.Mesh>>;
  transformControlRef: React.RefObject<TransformControlsImpl | null>;
}

/**
 * Draggable control point for Bezier curve
 */
export const ControlPoint = ({
  blockId,
  pointIndex,
  position,
  color,
  size,
  isActive,
  controlPointRefsRef,
  transformControlRef,
}: ControlPointProps) => {
  const meshRef = useRef<THREE.Mesh>(null);
  const { blocks } = useMotionStore();
  
  // Find the block to update
  const block = blocks.find(b => b.id === blockId);
  
  // Register mesh ref for raycaster detection
  useEffect(() => {
    if (meshRef.current && isActive) {
      const key = `${blockId}-${pointIndex}`;
      controlPointRefsRef.current.set(key, meshRef.current);
      
      return () => {
        controlPointRefsRef.current.delete(key);
      };
    }
  }, [blockId, pointIndex, isActive, controlPointRefsRef]);
  
  // Update position from store
  useFrame(() => {
    if (!meshRef.current) return;
    
    const currentPoint = block?.bezierCurve?.controlPoints?.[pointIndex];
    if (currentPoint) {
      const newPosition = new THREE.Vector3(...currentPoint);
      const currentObject = (transformControlRef.current as any)?.object;
      const isAttached = currentObject === meshRef.current;
      const isDragging = (transformControlRef.current as any)?.dragging ?? false;
      
      // Only update if not attached and not dragging (to avoid conflicts)
      if (!isAttached && !isDragging) {
        meshRef.current.position.copy(newPosition);
      }
    }
  });
  
  const currentObject = (transformControlRef.current as any)?.object;
  const isSelected = currentObject === meshRef.current;
  
  return (
    <group>
      {/* Control point sphere */}
      <Sphere
        ref={meshRef}
        position={position}
        args={[size, 16, 16]}
      >
        <meshStandardMaterial 
          color={color}
          emissive={isSelected ? color : undefined}
          emissiveIntensity={isSelected ? 0.5 : 0}
        />
      </Sphere>
      
      {/* Label */}
      <Text
        position={[position.x, position.y + 0.15, position.z]}
        fontSize={0.15}
        color={color}
        anchorX="center"
        anchorY="middle"
      >
        {pointIndex}
      </Text>
    </group>
  );
};

